// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  ProgressBar,
  RadioButton,
  RadioButtonGroup,
  Tag,
  Tile,
} from '@carbon/react';
import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { getTokenAsync } from '../config/authConfig';
import {
  streamOnboardingRun,
  streamOnboardingResume,
  type GatePayload,
} from '../services/onboardingService';
import './OnboardingDemo.css';

// A made-up tenant on the BFF's deprecated tenant routes: workarounds 4 and 5 in
// docs/design/obo-to-s2s/object-store-bff-workarounds.md.
const ONBOARDING_TENANT = 'helios-onboarding';
const DEFAULT_SITE_ID = '019e8469-25fe-7a59-93c3-afb2a7c00000';

type DemoState = 'upload' | 'processing' | 'question' | 'result';

// --- Classification payload types ---

interface ClassificationFile {
  file_id: string;
  filename: string;
  confidence_score: number;
  candidate_types: string[];
  rationale: string;
}

interface ClassificationSummaryItem {
  file_id: string;
  doc_type: string;
  confidence_score: number;
}

interface ClassificationPayload {
  stage: string;
  total_files: number;
  proceed_count: number;
  review_count: number;
  bundled_count: number;
  hitl_gate_1: ClassificationFile[];
  bundled_files: unknown[];
  classification_summary: ClassificationSummaryItem[];
  version_map: Record<string, string>;
  next_stage: string;
}

function tryParseClassification(text: string): ClassificationPayload | null {
  try {
    const trimmed = text.trim();
    const parsed = JSON.parse(trimmed);
    if (
      parsed &&
      typeof parsed === 'object' &&
      'stage' in parsed &&
      'classification_summary' in parsed
    ) {
      return parsed as ClassificationPayload;
    }
  } catch {
    // not valid JSON or not a classification payload
  }
  return null;
}

// --- HITL Gate payload types ---
// GatePayload/GateCandidateOption moved to onboardingService (shared with
// the onboarding wizard); this page keeps only its display-local types.

interface GateSelection {
  type: 'candidate' | 'other';
  option_id: string | null;
  doc_type: string;
}

function confidenceBadge(score: number): { label: string; className: string } {
  if (score >= 0.8) return { label: 'High', className: 'confidence-high' };
  if (score >= 0.6) return { label: 'Medium', className: 'confidence-medium' };
  return { label: 'Low', className: 'confidence-low' };
}

// Confidence indicator: high -> green Carbon Tag, low -> red Carbon Tag,
// medium -> a --sev2-tokened span (Carbon Tag has no orange variant).
function ConfidenceIndicator({ score }: { score: number }) {
  const badge = confidenceBadge(score);
  const text = `${badge.label} (${Math.round(score * 100)}%)`;
  if (badge.label === 'Medium') {
    return <span className="obd-conf-medium">{text}</span>;
  }
  return (
    <Tag type={badge.label === 'High' ? 'green' : 'red'} size="sm" className="obd-conf-tag">
      {text}
    </Tag>
  );
}

function ClassificationPreview({
  data,
  rawJson,
}: {
  data: ClassificationPayload;
  rawJson: string;
}) {
  const [showRaw, setShowRaw] = useState(false);

  const allFiles = data.classification_summary.map(summary => {
    const hitlFile = data.hitl_gate_1.find(f => f.file_id === summary.file_id);
    return { ...summary, ...hitlFile };
  });

  return (
    <div className="obd-preview">
      <div className="obd-preview-header">
        <h3>Classification Results</h3>
        <div className="obd-badges">
          <Tag type="blue" size="sm">
            {data.stage.replace(/_/g, ' ')}
          </Tag>
          {data.next_stage && (
            <Tag type="teal" size="sm">
              {data.next_stage.replace(/_/g, ' ')}
            </Tag>
          )}
        </div>
      </div>

      <div className="obd-stats">
        <Tile className="obd-stat">
          <span className="obd-stat-value">{data.total_files}</span>
          <span className="obd-stat-label">Total</span>
        </Tile>
        <Tile className="obd-stat obd-stat-proceed">
          <span className="obd-stat-value">{data.proceed_count}</span>
          <span className="obd-stat-label">Proceed</span>
        </Tile>
        <Tile className="obd-stat obd-stat-review">
          <span className="obd-stat-value">{data.review_count}</span>
          <span className="obd-stat-label">Review</span>
        </Tile>
        <Tile className="obd-stat obd-stat-bundled">
          <span className="obd-stat-value">{data.bundled_count}</span>
          <span className="obd-stat-label">Bundled</span>
        </Tile>
      </div>

      <div className="obd-files">
        {allFiles.map((file, i) => {
          const needsReview = data.hitl_gate_1.some(f => f.file_id === file.file_id);

          return (
            <div
              key={file.file_id || i}
              className={`obd-file-card ${needsReview ? 'obd-needs-review' : ''}`}
            >
              <div className="obd-file-card-header">
                <div className="obd-file-card-name">
                  <span className="obd-file-icon">&#128196;</span>
                  <span>{file.filename || file.file_id}</span>
                </div>
                {needsReview && (
                  <Tag type="warm-gray" size="sm" className="obd-review-tag">
                    Needs Review
                  </Tag>
                )}
              </div>

              <div className="obd-file-details">
                <div className="obd-detail-row">
                  <span className="obd-detail-label">Document Type</span>
                  <span className="obd-detail-value">{file.doc_type}</span>
                </div>
                <div className="obd-detail-row">
                  <span className="obd-detail-label">Confidence</span>
                  <span className="obd-detail-value">
                    <ConfidenceIndicator score={file.confidence_score} />
                  </span>
                </div>
                {file.rationale && (
                  <div className="obd-detail-row">
                    <span className="obd-detail-label">Rationale</span>
                    <span className="obd-detail-value obd-detail-rationale">{file.rationale}</span>
                  </div>
                )}
                {file.candidate_types && file.candidate_types.length > 0 && (
                  <div className="obd-detail-row">
                    <span className="obd-detail-label">Candidates</span>
                    <span className="obd-detail-value obd-candidate-chips">
                      {file.candidate_types.map((t, j) => (
                        <Tag key={j} type="cool-gray" size="sm">
                          {t}
                        </Tag>
                      ))}
                    </span>
                  </div>
                )}
              </div>

              {file.file_id && <div className="obd-file-card-id">ID: {file.file_id}</div>}
            </div>
          );
        })}
      </div>

      {data.version_map && Object.keys(data.version_map).length > 0 && (
        <div className="obd-versions">
          {Object.entries(data.version_map).map(([key, val]) => (
            <Tag key={key} type="purple" size="sm" className="obd-version-chip">
              {key}: {val}
            </Tag>
          ))}
        </div>
      )}

      <div className="obd-rawjson-section">
        <button
          className="obd-rawjson-toggle"
          onClick={() => setShowRaw(!showRaw)}
          aria-expanded={showRaw}
        >
          <span className={`obd-toggle-arrow ${showRaw ? 'open' : ''}`}>&#9654;</span>
          Raw JSON
        </button>
        {showRaw && <pre className="obd-rawjson">{rawJson}</pre>}
      </div>
    </div>
  );
}

interface FileUploadStatus {
  file: File;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'done' | 'error';
  objectKey?: string;
}

function sanitizeKey(filename: string): string {
  return filename.replace(/[^a-zA-Z0-9_./-]/g, '_');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OnboardingDemo() {
  const [state, setState] = useState<DemoState>('upload');
  const [files, setFiles] = useState<File[]>([]);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [gateData, setGateData] = useState<GatePayload | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [gateSelection, setGateSelection] = useState<GateSelection | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadToObjectStore = async (
    file: File,
    token: string,
    onProgress: (_pct: number) => void,
    keyOverride?: string
  ): Promise<{ key: string }> => {
    const safeKey = keyOverride ?? sanitizeKey(file.name);
    const uploadUrl = getApiUrl(ENDPOINTS.OBJECT_STORE_UPLOAD(safeKey));

    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('X-Tenant-ID', ONBOARDING_TENANT);

      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ key: data.data?.key || safeKey });
          } catch {
            resolve({ key: safeKey });
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(formData);
    });
  };

  // Workflow SSE consumption lives in onboardingService (shared with the
  // onboarding wizard); these handlers map its events onto this page's state.
  const runStreamHandlers = (onSessionId?: (_id: string) => void) => ({
    onSessionId: (id: string) => {
      setConversationId(id);
      onSessionId?.(id);
    },
    onGate: (gate: GatePayload) => {
      setGateData(gate);
      setGateSelection(null);
      setState('question');
    },
    onError: (message: string) => setError(message),
    onContent: (content: string) => {
      setStreamText(prev => prev + content);
      if (streamRef.current) {
        streamRef.current.scrollTop = streamRef.current.scrollHeight;
      }
    },
    onDone: () => {},
  });

  const handleSubmit = async () => {
    if (files.length === 0) return;

    setError(null);
    setStreamText('');
    setState('processing');
    setIsStreaming(false);

    const statuses: FileUploadStatus[] = files.map(file => ({
      file,
      progress: 0,
      status: 'pending',
    }));
    setFileStatuses(statuses);

    let token: string;
    try {
      token = await getTokenAsync();
    } catch {
      setError('Authentication failed. Please try again.');
      setState('upload');
      return;
    }

    const submissionId = crypto.randomUUID();

    // Upload each file sequentially with UUID-prefixed keys
    const uploadedFiles: Array<{
      uri: string;
      filename: string;
      content_type: string;
      size_bytes: number;
    }> = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setFileStatuses(prev =>
        prev.map((s, idx) => (idx === i ? { ...s, status: 'uploading' } : s))
      );

      try {
        const key = `${submissionId}/${sanitizeKey(file.name)}`;
        await uploadToObjectStore(
          file,
          token,
          pct => {
            setFileStatuses(prev =>
              prev.map((s, idx) => (idx === i ? { ...s, progress: pct } : s))
            );
          },
          key
        );

        setFileStatuses(prev =>
          prev.map((s, idx) =>
            idx === i ? { ...s, progress: 100, status: 'done', objectKey: key } : s
          )
        );

        uploadedFiles.push({
          uri: `mcp-object-store://${key}`,
          filename: file.name,
          content_type: file.type || 'application/pdf',
          size_bytes: file.size,
        });
      } catch (err) {
        setFileStatuses(prev => prev.map((s, idx) => (idx === i ? { ...s, status: 'error' } : s)));
        setError(`Failed to upload ${file.name}: ${(err as Error).message}`);
        setState('upload');
        return;
      }
    }

    // All files uploaded — persist payload then trigger the workflow
    setIsStreaming(true);
    const payload = {
      site_id: DEFAULT_SITE_ID,
      files: uploadedFiles,
    };

    try {
      const payloadBlob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const payloadFile = new File([payloadBlob], 'payload.json', {
        type: 'application/json',
      });
      await uploadToObjectStore(
        payloadFile,
        token,
        () => {},
        `onboarding/${DEFAULT_SITE_ID}/payload.json`
      );
    } catch {
      // Payload persistence is best-effort — don't block the workflow
    }

    let returnedSessionId: string | null = null;
    await streamOnboardingRun(
      payload,
      runStreamHandlers(id => {
        returnedSessionId = id;
      })
    );

    if (returnedSessionId) {
      try {
        const convBlob = new Blob([JSON.stringify({ conversation_id: returnedSessionId })], {
          type: 'application/json',
        });
        const convFile = new File([convBlob], 'conversation_id.json', {
          type: 'application/json',
        });
        await uploadToObjectStore(
          convFile,
          token,
          () => {},
          `onboarding/${DEFAULT_SITE_ID}/conversation_id.json`
        );
      } catch {
        // Conversation ID persistence is best-effort
      }
    }

    setIsStreaming(false);
    // Only transition to result if we're not paused on a question
    setState(prev => (prev === 'question' ? prev : 'result'));
  };

  const handleResume = async () => {
    if (!gateSelection || !conversationId || !gateData) return;

    const gateResponse = {
      gate_id: gateData.gate_id,
      workflow_instance_id: gateData.workflow_instance_id,
      file_id: gateData.file_id,
      operator_response: {
        response_type: gateSelection.type === 'candidate' ? 'candidate_selected' : 'other_selected',
        selected_option_id: gateSelection.option_id,
        selected_doc_type: gateSelection.doc_type,
      },
    };

    setGateData(null);
    setState('processing');
    setIsStreaming(true);
    setError(null);

    await streamOnboardingResume(
      DEFAULT_SITE_ID,
      conversationId,
      gateResponse,
      runStreamHandlers()
    );

    setIsStreaming(false);
    setState(prev => (prev === 'question' ? prev : 'result'));
  };

  const handleReset = () => {
    setFiles([]);
    setFileStatuses([]);
    setStreamText('');
    setError(null);
    setIsStreaming(false);
    setGateData(null);
    setConversationId(null);
    setGateSelection(null);
    setState('upload');
  };

  const classificationData = useMemo(() => tryParseClassification(streamText), [streamText]);

  // The HITL gate renders two visual RadioButtonGroups (suggested candidates +
  // "other" types) that share one logical selection. Each RadioButton carries a
  // composite value (`candidate:<option_id>` / `other:<docType>`); onChange
  // parses it back into the existing GateSelection shape handleResume sends.
  const handleGateChange = useCallback(
    (value: string | number | undefined) => {
      if (typeof value !== 'string') return;
      const sep = value.indexOf(':');
      const kind = value.slice(0, sep);
      const rest = value.slice(sep + 1);
      if (kind === 'candidate') {
        const opt = gateData?.candidate_options.find(o => o.option_id === rest);
        if (opt) {
          setGateSelection({ type: 'candidate', option_id: opt.option_id, doc_type: opt.doc_type });
        }
      } else if (kind === 'other') {
        setGateSelection({ type: 'other', option_id: null, doc_type: rest });
      }
    },
    [gateData]
  );

  return (
    <div className="obd">
      <h1 className="obd-title">Onboarding Workflow</h1>
      <p className="obd-subtitle">
        Upload PDF documents to process through the onboarding workflow (Site: {DEFAULT_SITE_ID})
      </p>

      {error && (
        <InlineNotification
          className="obd-notification"
          kind="error"
          title="Error"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      {state === 'upload' && (
        <div className="obd-card">
          <FileUploaderDropContainer
            accept={['application/pdf', '.pdf']}
            multiple
            labelText="Drag and drop PDF files here or click to browse"
            onAddFiles={(_evt, { addedFiles }) => {
              setFiles(prev => [...prev, ...addedFiles.filter(f => f.type === 'application/pdf')]);
            }}
          />

          {files.length > 0 && (
            <div className="obd-file-list">
              {files.map((file, i) => (
                <FileUploaderItem
                  key={`${file.name}-${i}`}
                  name={`${file.name} — ${formatBytes(file.size)}`}
                  status="edit"
                  iconDescription={`Remove ${file.name}`}
                  onDelete={() => removeFile(i)}
                />
              ))}
            </div>
          )}

          <div className="obd-actions">
            <Button kind="primary" disabled={files.length === 0} onClick={handleSubmit}>
              Upload &amp; Process
            </Button>
          </div>
        </div>
      )}

      {state === 'processing' && (
        <div className="obd-card">
          <div className="obd-progress">
            <h3 className="obd-section-title">Uploading files</h3>
            {fileStatuses.map((fs, i) => (
              <ProgressBar
                key={i}
                className="obd-progress-bar"
                label={fs.file.name}
                value={fs.progress}
                max={100}
                status={
                  fs.status === 'done' ? 'finished' : fs.status === 'error' ? 'error' : 'active'
                }
                helperText={`${fs.progress}%`}
              />
            ))}
          </div>

          {isStreaming && (
            <div className="obd-stream-output">
              <h3 className="obd-section-title">Workflow output</h3>
              <div className="obd-stream" ref={streamRef}>
                {streamText}
                <span className="obd-cursor" />
              </div>
            </div>
          )}
        </div>
      )}

      {state === 'question' && gateData && (
        <div className="obd-card">
          {streamText && (
            <div className="obd-stream-output">
              <h3 className="obd-section-title">Workflow output</h3>
              <div className="obd-stream" ref={streamRef}>
                {streamText}
              </div>
            </div>
          )}
          <div className="obd-question">
            <div className="obd-gate-header">
              <h3>{gateData.question_prompt}</h3>
              <div className="obd-gate-file-badge">
                <span className="obd-file-icon">&#128196;</span>
                <span>{gateData.filename}</span>
              </div>
            </div>

            <div className="obd-gate-group">
              <RadioButtonGroup
                name="hitl-candidates"
                legendText="Suggested document types"
                orientation="vertical"
                valueSelected={
                  gateSelection?.type === 'candidate'
                    ? `candidate:${gateSelection.option_id}`
                    : undefined
                }
                onChange={handleGateChange}
              >
                {gateData.candidate_options.map(opt => (
                  <RadioButton
                    key={opt.option_id}
                    value={`candidate:${opt.option_id}`}
                    labelText={
                      <span className="obd-gate-option">
                        <span className="obd-gate-option-header">
                          <span className="obd-gate-doc-type">{opt.doc_type}</span>
                          <ConfidenceIndicator score={opt.confidence_score} />
                        </span>
                        <span className="obd-gate-rationale">{opt.rationale}</span>
                      </span>
                    }
                  />
                ))}
              </RadioButtonGroup>
            </div>

            {gateData.allow_other && gateData.other_options.length > 0 && (
              <div className="obd-gate-group obd-gate-other">
                <RadioButtonGroup
                  name="hitl-other"
                  legendText="Other document types"
                  orientation="vertical"
                  valueSelected={
                    gateSelection?.type === 'other' ? `other:${gateSelection.doc_type}` : undefined
                  }
                  onChange={handleGateChange}
                >
                  {gateData.other_options.map(docType => (
                    <RadioButton key={docType} value={`other:${docType}`} labelText={docType} />
                  ))}
                </RadioButtonGroup>
              </div>
            )}

            <div className="obd-actions">
              <Button kind="primary" disabled={!gateSelection} onClick={handleResume}>
                Confirm Classification
              </Button>
            </div>
          </div>
        </div>
      )}

      {state === 'result' && (
        <div className="obd-card">
          <h2 className="obd-result-title">Workflow Complete</h2>
          {classificationData ? (
            <ClassificationPreview
              data={classificationData}
              rawJson={JSON.stringify(classificationData, null, 2)}
            />
          ) : (
            <div className="obd-result-text">{streamText || '(No output received)'}</div>
          )}
          <div className="obd-actions">
            <Button kind="primary" onClick={handleReset}>
              Upload Another
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
